import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { BrandMark } from "@/components/BrandMark";
import PasswordChangeDialog from "@/components/PasswordChangeDialog";
import { LayoutGrid, PlusCircle, LogOut, Moon, Sun, ShieldCheck, Users, Menu, X } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, active, testid, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-3 px-4 py-3 border-l-2 transition-colors duration-150 text-sm font-medium ${
      active
        ? "border-primary bg-muted text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
    }`}
  >
    <Icon size={18} strokeWidth={2} />
    {label}
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [path]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Mobile Top Bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Buka menu navigasi"
            className="p-2 -ml-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <div>
              <div className="font-heading font-black text-base tracking-tight leading-none">PLAZA KREASI</div>
              <div className="text-[9px] tracking-[0.2em] text-muted-foreground uppercase">Minutes System</div>
            </div>
          </Link>
        </div>
        <button
          onClick={toggle}
          aria-label="Toggle theme"
          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </header>

      {/* Backdrop for Mobile Drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Drawer on Mobile, Fixed Sidebar on Desktop */}
      <aside
        className={`fixed top-0 bottom-0 left-0 w-64 border-r border-border flex flex-col bg-background z-50 transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2" data-testid="brand-logo">
            <BrandMark />
            <div>
              <div className="font-heading font-black text-lg tracking-tight leading-none">PLAZA KREASI</div>
              <div className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase">Minutes System</div>
            </div>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
            aria-label="Tutup menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 py-4 flex flex-col gap-1 overflow-y-auto">
          <NavItem to="/" icon={LayoutGrid} label="Meeting History" active={path === "/"} testid="nav-history" onClick={() => setMobileMenuOpen(false)} />
          <NavItem to="/new" icon={PlusCircle} label="New Meeting" active={path === "/new"} testid="nav-new" onClick={() => setMobileMenuOpen(false)} />
          {user?.role === "admin" && <>
            <NavItem to="/admin" icon={ShieldCheck} label="Control Panel" active={path === "/admin"} testid="nav-admin" onClick={() => setMobileMenuOpen(false)} />
            <NavItem to="/admin/users" icon={Users} label="Master Pengguna" active={path === "/admin/users"} testid="nav-master-users" onClick={() => setMobileMenuOpen(false)} />
          </>}
        </nav>

        <div className="border-t border-border p-4 flex flex-col gap-3">
          <PasswordChangeDialog />
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email || user?.username}</div>
              {user?.department && <div className="text-xs text-muted-foreground truncate" title={user.department}>{user.department}</div>}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 w-full lg:ml-64 min-h-screen">{children}</main>
    </div>
  );
};
